import React from "react";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Font,
  Image,
} from "@react-pdf/renderer";
import { IproductRegistration } from "../../models/epcCustomer.model";
import sign1 from "../../assets/signs/sign-1.png";
import sign2 from "../../assets/signs/sign-2.png";

// 1. REGISTER FONT
Font.register({
  family: "Roboto",
  src: "https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-light-webfont.ttf",
  fonts: [
    {
      src: "https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-regular-webfont.ttf",
      fontWeight: "normal",
    },
    {
      src: "https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-bold-webfont.ttf",
      fontWeight: "bold",
    },
  ],
});

const styles = StyleSheet.create({
  page: {
    padding: 25,
    fontSize: 10, // REVERTED TO 10 TO FIT ON ONE PAGE
    fontFamily: "Roboto",
    lineHeight: 1.3,
  },
  header: {
    fontSize: 12, // REVERTED TO 12
    fontWeight: "bold",
    textAlign: "center",
    marginBottom: 10,
    textDecoration: "underline",
  },
  paragraph: {
    marginBottom: 8, // Reduced slightly to save space
    textAlign: "justify",
    fontSize: 10,
  },
  sectionTitle: {
    fontSize: 10,
    fontWeight: "bold",
    marginTop: 8,
    marginBottom: 4,
  },
  // Table Styles
  table: {
    display: "flex",
    width: "auto",
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: "#000",
    marginBottom: 8,
  },
  tableRow: {
    margin: "auto",
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#000",
    width: "100%",
  },
  tableCol: {
    borderRightWidth: 1,
    borderRightColor: "#000",
    padding: 4,
    justifyContent: "center",
  },
  tableColLast: {
    padding: 4,
    justifyContent: "center",
  },
  tableHeader: {
    fontWeight: "bold",
    textAlign: "center",
  },
  tableCell: {
    fontSize: 9, // Slightly smaller table text
  },
  colNo: { width: "10%" },
  colParticular: { width: "30%" },
  colData: { width: "30%" },

  consumerContainer: {
    flexDirection: "row",
    borderWidth: 1,
    borderColor: "#000",
    marginBottom: 8,
  },
  consumerLeft: {
    width: "50%",
    borderRightWidth: 1,
    borderRightColor: "#000",
    padding: 5,
  },
  consumerRight: {
    width: "50%",
    padding: 5,
  },

  // Test Results Box
  testResultContainer: {
    flexDirection: "row",
    borderWidth: 1,
    borderColor: "#000",
  },
  testHalf: {
    width: "50%",
  },
  testHalfBorder: {
    width: "50%",
    borderRightWidth: 1,
    borderRightColor: "#000",
  },
  // Inner cells for Test Results to create visible lines
  testRowCell: {
    padding: 5,
    borderBottomWidth: 1,
    borderBottomColor: "#000",
    height: 25, // Fixed height for alignment
    justifyContent: "center",
  },
  testRowCellLast: {
    padding: 5,
    borderBottomWidth: 0,
    justifyContent: "center",
  },
  bold: {
    fontWeight: "bold",
  },
  footer: {
    marginTop: 30, // Reduced margin to pull signature up
    flexDirection: "row",
    justifyContent: "space-between",
  },
  footerSignature: {
    width: "45%",
    borderTopWidth: 1,
    borderTopColor: "#000",
    paddingTop: 5,
    textAlign: "center",
    fontSize: 9,
  },
  signatureContainer: {
    width: "45%",
    alignItems: "center",
  },

  signatureLine: {
    width: "100%",
    borderTopWidth: 1,
    borderTopColor: "#000",
    marginBottom: 0,
  },

  signatureTitle: {
    fontSize: 9,
    textAlign: "center",
    marginBottom: 0,
  },

  signatureImage: {
    width: 235,
    height: 85,
    objectFit: "contain",
  },
  signatureImage2: {
    width: 270,
    height: 100,
    objectFit: "contain",
  },
});

interface SelfCertificationPdfProps {
  productRegistration: IproductRegistration;
}

export const SelfCertificationPdf: React.FC<SelfCertificationPdfProps> = ({
  productRegistration,
}) => {
  const formatDate = (dateStr?: string | Date | null) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "-";

    return `${d.getDate().toString().padStart(2, "0")}/${(d.getMonth() + 1)
      .toString()
      .padStart(2, "0")}/${d.getFullYear()}`;
  };

  const inverterSrNo =
    productRegistration.inverterBarcodes &&
    productRegistration.inverterBarcodes.length > 0
      ? productRegistration.inverterBarcodes[0]
      : "-";

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <Text style={styles.header}>
          Self-Certification for Solar Roof top Installations up to 10KW
        </Text>

        <Text style={styles.paragraph}>
          This is to certify that the installation of Solar roof top power plant
          along with its associated equipment of capacity{" "}
          <Text style={styles.bold}>
            {productRegistration.panelCapacity || "-"} X{" "}
            {productRegistration.numberOfModules || "-"} ={" "}
            {productRegistration.totalSolarCapacity || "-"}
          </Text>{" "}
          At:{" "}
          <Text style={styles.bold}>
            {productRegistration.address || "-"},{" "}
            {productRegistration.area || "-"}
          </Text>{" "}
          has been carried out by us/me and the details of the Installation as
          well as the test results are as under:
        </Text>

        {/* 1. Details of Consumer */}
        <Text style={styles.sectionTitle}>1. Details of Consumer:</Text>
        <View style={styles.consumerContainer}>
          <View style={styles.consumerLeft}>
            <Text style={styles.tableCell}>
              <Text style={styles.bold}>Name : - </Text>
              {productRegistration.name}
            </Text>
            <Text style={[styles.tableCell, { marginTop: 5 }]}>
              <Text style={styles.bold}>Electricity Connection No. : - </Text>
              {productRegistration.consumerNumber}
            </Text>
            <Text style={[styles.tableCell, { marginTop: 5 }]}>
              <Text style={styles.bold}>Registration No.: - </Text>
              {(productRegistration as any).registrationNumber || "-"}
            </Text>
          </View>
          <View style={styles.consumerRight}>
            <Text style={styles.tableCell}>
              <Text style={styles.bold}>Address :- </Text>
              {productRegistration.address}, {productRegistration.area}
            </Text>
          </View>
        </View>

        {/* 2. Details of Solar PV cells and Inverter */}
        <Text style={styles.sectionTitle}>
          2. Details of Solar PV cells and Inverter
        </Text>
        <View style={styles.table}>
          <View style={styles.tableRow}>
            <View style={[styles.tableCol, styles.colNo, styles.tableHeader]}>
              <Text style={styles.tableCell}>No.</Text>
            </View>
            <View
              style={[
                styles.tableCol,
                styles.colParticular,
                styles.tableHeader,
              ]}
            >
              <Text style={styles.tableCell}>Particular</Text>
            </View>
            <View style={[styles.tableCol, styles.colData, styles.tableHeader]}>
              <Text style={styles.tableCell}>Modules</Text>
            </View>
            <View
              style={[styles.tableColLast, styles.colData, styles.tableHeader]}
            >
              <Text style={styles.tableCell}>Inverter</Text>
            </View>
          </View>

          {/* Row 1: Make */}
          <View style={styles.tableRow}>
            <View style={[styles.tableCol, styles.colNo]}>
              <Text style={[styles.tableCell, { textAlign: "center" }]}>
                1.
              </Text>
            </View>
            <View style={[styles.tableCol, styles.colParticular]}>
              <Text style={styles.tableCell}>Make</Text>
            </View>
            <View style={[styles.tableCol, styles.colData]}>
              <Text style={[styles.tableCell, { textAlign: "center" }]}>
                {productRegistration.panelMake || "-"}
              </Text>
            </View>
            <View style={[styles.tableColLast, styles.colData]}>
              <Text style={[styles.tableCell, { textAlign: "center" }]}>
                {productRegistration.inverterMake || "-"}
              </Text>
            </View>
          </View>

          {/* Row 2: Capacity */}
          <View style={styles.tableRow}>
            <View style={[styles.tableCol, styles.colNo]}>
              <Text style={[styles.tableCell, { textAlign: "center" }]}>
                2.
              </Text>
            </View>
            <View style={[styles.tableCol, styles.colParticular]}>
              <Text style={styles.tableCell}>Capacity</Text>
            </View>
            <View style={[styles.tableCol, styles.colData]}>
              <Text style={[styles.tableCell, { textAlign: "center" }]}>
                {productRegistration.panelCapacity || "-"}
              </Text>
            </View>
            <View style={[styles.tableColLast, styles.colData]}>
              <Text style={[styles.tableCell, { textAlign: "center" }]}>
                {productRegistration.inverterCapacity || "-"}
              </Text>
            </View>
          </View>

          {/* Row 3: No of Modules/Inverter */}
          <View style={styles.tableRow}>
            <View style={[styles.tableCol, styles.colNo]}>
              <Text style={[styles.tableCell, { textAlign: "center" }]}>
                3.
              </Text>
            </View>
            <View style={[styles.tableCol, styles.colParticular]}>
              <Text style={styles.tableCell}>No. of Modules/Inverter</Text>
            </View>
            <View style={[styles.tableCol, styles.colData]}>
              <Text style={[styles.tableCell, { textAlign: "center" }]}>
                {productRegistration.numberOfModules || "-"}
              </Text>
            </View>
            <View style={[styles.tableColLast, styles.colData]}>
              <Text style={[styles.tableCell, { textAlign: "center" }]}>
                01
              </Text>
            </View>
          </View>

          {/* Row 4: Total Capacity */}
          <View style={styles.tableRow}>
            <View style={[styles.tableCol, styles.colNo]}>
              <Text style={[styles.tableCell, { textAlign: "center" }]}>
                4.
              </Text>
            </View>
            <View style={[styles.tableCol, styles.colParticular]}>
              <Text style={styles.tableCell}>Total Capacity</Text>
            </View>
            <View style={[styles.tableCol, styles.colData]}>
              <Text style={[styles.tableCell, { textAlign: "center" }]}>
                {productRegistration.totalSolarCapacity || "-"}
              </Text>
            </View>
            <View style={[styles.tableColLast, styles.colData]}>
              <Text style={[styles.tableCell, { textAlign: "center" }]}>
                {productRegistration.inverterCapacity || "-"}
              </Text>
            </View>
          </View>

          {/* Row 5: Voltage */}
          <View style={styles.tableRow}>
            <View style={[styles.tableCol, styles.colNo]}>
              <Text style={[styles.tableCell, { textAlign: "center" }]}>
                5.
              </Text>
            </View>
            <View style={[styles.tableCol, styles.colParticular]}>
              <Text style={styles.tableCell}>Voltage</Text>
            </View>
            <View style={[styles.tableCol, styles.colData]}>
              <Text style={[styles.tableCell, { textAlign: "center" }]}>
                {(productRegistration as any).panelVoltage || "-"}
              </Text>
            </View>
            <View style={[styles.tableColLast, styles.colData]}>
              <Text style={[styles.tableCell, { textAlign: "center" }]}>
                {(productRegistration as any).inverterVoltage || "-"}
              </Text>
            </View>
          </View>

          {/* Row 6: Sr No */}
          <View style={[styles.tableRow, { borderBottomWidth: 0 }]}>
            <View style={[styles.tableCol, styles.colNo]}>
              <Text style={[styles.tableCell, { textAlign: "center" }]}>
                6.
              </Text>
            </View>
            <View style={[styles.tableCol, styles.colParticular]}>
              <Text style={styles.tableCell}>Sr. No</Text>
            </View>
            <View style={[styles.tableCol, styles.colData]}>
              <Text style={[styles.tableCell, { textAlign: "center" }]}>
                Attached separate sheet
              </Text>
            </View>
            <View style={[styles.tableColLast, styles.colData]}>
              <Text style={[styles.tableCell, { textAlign: "center" }]}>
                {inverterSrNo}
              </Text>
            </View>
          </View>
        </View>

        {/* 3. Test Results */}
        <Text style={styles.sectionTitle}>3. Test Results:</Text>
        <View style={styles.testResultContainer}>
          {/* --- LEFT SIDE: EARTHING --- */}
          <View style={styles.testHalfBorder}>
            {/* ROW 1: Earthing Title (Centered, Bold) */}
            <View style={[styles.testRowCell, { alignItems: "center" }]}>
              <Text style={[styles.tableCell, styles.bold]}>Earthing</Text>
            </View>

            {/* ROW 2: Earth Tester Sr. No (Left, Normal) */}
            <View style={styles.testRowCell}>
              <Text style={styles.tableCell}>Earth Tester Sr. no.-</Text>
            </View>

            {/* ROW 3: Values (Left, Normal) */}
            <View style={styles.testRowCellLast}>
              <Text style={[styles.tableCell, { marginBottom: 2 }]}>
                Earth Resistance values for all Earth Pits-
              </Text>
              <View style={{ flexDirection: "row", marginTop: 2 }}>
                <Text style={[styles.tableCell, { width: "50%" }]}>
                  1. 0.9 Ω
                </Text>
                <Text style={[styles.tableCell, { width: "50%" }]}>
                  3. 0.7 Ω
                </Text>
              </View>
              <View style={{ flexDirection: "row", marginTop: 2 }}>
                <Text style={[styles.tableCell, { width: "50%" }]}>
                  2. 0.86 Ω
                </Text>
                <Text style={[styles.tableCell, { width: "50%" }]}>
                  4. ______
                </Text>
              </View>
            </View>
          </View>

          {/* --- RIGHT SIDE: INSULATION --- */}
          <View style={styles.testHalf}>
            {/* ROW 1: Header (Matching layout) */}
            <View style={[styles.testRowCell, { alignItems: "center" }]}>
              <Text style={[styles.tableCell, styles.bold]}>
                Insulation resistance
              </Text>
            </View>

            {/* ROW 2: Meter details */}
            <View style={styles.testRowCell}>
              <Text style={styles.tableCell}>Meegar Sr. no and voltage:</Text>
            </View>

            {/* ROW 3: Values */}
            <View style={styles.testRowCellLast}>
              <Text style={[styles.tableCell, { marginBottom: 2 }]}>
                Insulation Resistance:
              </Text>
              <Text style={[styles.tableCell, { marginTop: 2 }]}>
                1.Phase to Phase: 415 MΩ
              </Text>
              <Text style={[styles.tableCell, { marginTop: 2 }]}>
                2.Phase to Earth: 420 MΩ
              </Text>
            </View>
          </View>
        </View>

        {/* Declarations */}
        <View style={{ marginTop: 10 }}>
          <Text style={styles.paragraph}>
            The work of aforesaid installation has been completed by us on{" "}
            <Text>{formatDate(productRegistration.installationDate)}</Text> and
            it is to hereby declare that
          </Text>
          <Text style={[styles.paragraph, { fontSize: 9, marginBottom: 5 }]}>
            a) All PV modules and its supporting structures have enough
            mechanical strength and it conforms to the relevant codes/guidelines
            prescribed in this behalf.
          </Text>
          <Text style={[styles.paragraph, { fontSize: 9, marginBottom: 5 }]}>
            b) All cables/wires, protective switchgears as well as Earthlings
            are of adequate ratings/size and they conforms to the requirements
            of Central Electricity Authority (Measures relating to safety and
            electrical supply), Regulations 2010 as well as the relevant
            codes/guidelines prescribed in this behalf.
          </Text>
          <Text style={[styles.paragraph, { fontSize: 9 }]}>
            c) The work of aforesaid Installation has been carried out in
            conformance with the requirements of Central Electricity Authority
            (Measures relating to safety and electrical supply), Regulations
            2010 and the relevant codes/guidelines prescribed in this behalf.
            The installation is tested by us and is found safe to be energized.
          </Text>
        </View>

        {/* Footer / Signatures */}
        <View style={styles.footer} wrap={false}>
          <View style={styles.signatureContainer}>
            <View style={styles.signatureLine} />
            <Text style={styles.signatureTitle}>
              Stamp & Signature of Electrical Supervisor
            </Text>

            <Image src={sign1} style={styles.signatureImage} />
          </View>

          <View style={styles.signatureContainer}>
            <View style={styles.signatureLine} />
            <Text style={styles.signatureTitle}>
              Stamp & Signature of Electrical Contractor
            </Text>

            <Image src={sign2} style={styles.signatureImage2} />
          </View>
        </View>
      </Page>
    </Document>
  );
};
